/**
 * Carrito lateral de Ritualic — v2
 *
 * La mecanica viene de la bolsa de VOLTAJE (`vt-cart-drawer.liquid`); la logica
 * de negocio es la de Ritualic. Ver el comentario de sections/rit-cart-drawer.liquid.
 *
 * Todo lo que se ve lo pinta Shopify: cada llamada al carrito pide de vuelta la
 * propia seccion (`sections=rit-cart-drawer`) y aqui solo se reemplazan las zonas
 * marcadas con `data-cd-live`. Asi el precio, el formato y los descuentos son los
 * mismos que en el resto de la tienda, sin reconstruirlos a mano en JS.
 *
 * Dos cosas vienen de la v1 y no se tocan, porque costaron medirlas (spec,
 * 2026-09-13):
 *  1. Horizon avisa de forma optimista, ANTES de que termine el POST a
 *     `/cart/add.js`. Se espera `e.promise` y despues se pide el carrito.
 *  2. El contador del header se escribe a mano y tres veces; y nunca se despacha
 *     `shopify:cart:lines-update` desde aqui, porque Horizon lo suma otra vez.
 */

/**
 * El contador del header lo lleva `cart-icon.js` de Horizon, que escucha el
 * evento estandar de Shopify pero espera su clase de evento, no un CustomEvent
 * cualquiera. En vez de imitar esa forma interna, se escribe directamente en la
 * burbuja: es un numero y una clase, y no depende de detalles del tema que
 * pueden cambiar en la proxima version.
 */
function ritEscribirBurbuja(cantidad) {
  document.querySelectorAll('.cart-bubble').forEach((burbuja) => {
    burbuja.classList.toggle('visually-hidden', cantidad === 0);
    const cifra = burbuja.querySelector('[ref="cartBubbleCount"]');
    if (!cifra) return;
    cifra.classList.toggle('hidden', cantidad === 0);
    cifra.textContent = cantidad < 100 ? String(cantidad) : '';
  });
}

/**
 * El contador de Horizon tambien corre su propia carrera: reacciona al evento
 * optimista y vuelve a pintar el header con el carrito viejo, a veces DESPUES de
 * que nosotros hayamos escrito el valor bueno. Medido: con un articulo en el
 * carrito la burbuja se quedaba en 0.
 *
 * Se escribe tres veces —ahora, en el siguiente fotograma y medio segundo
 * despues— para caer tambien del otro lado de su repintado. Se vuelve a buscar
 * el elemento en cada pasada porque Horizon lo reemplaza, no lo edita.
 */
function ritActualizarBurbuja(cantidad) {
  ritEscribirBurbuja(cantidad);
  requestAnimationFrame(() => ritEscribirBurbuja(cantidad));
  setTimeout(() => ritEscribirBurbuja(cantidad), 500);
}

class RitCartDrawer extends HTMLElement {
  connectedCallback() {
    if (this.listo) return;
    this.listo = true;

    this.sid = this.dataset.sectionId;
    try {
      this.cfg = JSON.parse(this.querySelector('[data-cd-config]').textContent);
    } catch {
      this.cfg = { rutas: {}, t: {} };
    }
    this.t = this.cfg.t || {};

    this.panel = this.querySelector('[data-rit-cd="panel"]');
    this.velo = this.querySelector('[data-rit-cd="velo"]');
    this.scroll = this.querySelector('[data-cd-scroll]');
    this.agregado = this.querySelector('[data-cd-agregado]');
    this.barraDeshacer = this.querySelector('[data-cd-deshacer-barra]');
    this.estado = this.querySelector('[data-cd-estado]');
    this.recs = this.querySelector('[data-cd-recs]');
    this.pista = this.recs?.querySelector('[data-cd-recs-pista]');
    this.recsTitulo = this.recs?.querySelector('[data-cd-recs-titulo]');

    this.abierto = false;
    this.ancla = null;
    this.recsClave = '';
    this.infoDeshacer = null;
    this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.velo?.addEventListener('click', () => this.cerrar());
    this.addEventListener('click', (e) => this.alClic(e));
    // En el documento y no en el cajon: al repintarse una linea, el boton que
    // tenia el foco desaparece, el foco cae al body y un keydown escuchado aqui
    // ya no llegaba. Resultado medido: Escape no cerraba tras "Pasate a 500 mL".
    document.addEventListener('keydown', (e) => {
      if (this.abierto) this.alTecla(e);
    });
    this.addEventListener('submit', (e) => this.alEnviar(e));
    this.pista?.addEventListener('scroll', () => this.flechas(), { passive: true });
    this.deslizarParaCerrar();

    // El boton de agregar es el de Horizon; el aviso es el evento estandar de
    // Shopify. Trae `lines` (que variante y cuanta) y `promise` (cuando acaba).
    document.addEventListener('shopify:cart:lines-update', (e) => {
      const accion = e.action ?? e.detail?.action;
      if (accion === 'add') return this.alAgregar(e);
      // Otro componente cambio el carrito (la pagina de carrito, por ejemplo).
      if (this.abierto) Promise.resolve(e.promise).catch(() => {}).then(() => this.refrescar());
    });

    // El icono del header y cualquier otro disparador propio.
    document.addEventListener('click', (e) => {
      const disparador = e.target.closest('[data-rit-cd-abrir]');
      if (!disparador) return;
      e.preventDefault();
      this.abrir(disparador);
      this.refrescar();
    });

    // Volver con el boton atras puede traer la pagina congelada con un carrito viejo.
    window.addEventListener('pageshow', (e) => {
      if (e.persisted && this.abierto) this.refrescar();
    });

    document.addEventListener('shopify:section:select', (e) => {
      if (e.detail?.sectionId === this.sid) this.abrir();
    });
    document.addEventListener('shopify:section:deselect', (e) => {
      if (e.detail?.sectionId === this.sid) this.cerrar();
    });
  }

  /* ── Abrir y cerrar ── */

  abrir(disparador) {
    this.ultimoDisparador = disparador || document.activeElement;
    if (this.abierto) return;
    this.abierto = true;
    this.panel?.classList.add('is-open');
    this.velo?.classList.add('is-open');
    document.body.classList.add('rit-cd-abierto');
    this.panel?.focus({ preventScroll: true });
    // Mientras carga, el carrito que hay en pantalla es el viejo: las
    // sugerencias se piden cuando llegue el nuevo, desde `aplicar`.
    if (!this.classList.contains('esta-cargando')) this.cargarRecs();
  }

  cerrar() {
    if (!this.abierto) return;
    this.abierto = false;
    this.panel?.classList.remove('is-open');
    this.velo?.classList.remove('is-open');
    document.body.classList.remove('rit-cd-abierto');
    const vuelta = this.ultimoDisparador;
    if (vuelta?.focus && document.contains(vuelta)) vuelta.focus({ preventScroll: true });
  }

  alTecla(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.cerrar();
      return;
    }
    if (e.key !== 'Tab' || !this.abierto) return;
    const enfocables = [
      ...this.panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'),
    ].filter((el) => el.offsetParent !== null && !el.closest('[hidden]'));
    if (!enfocables.length) return;
    const primero = enfocables[0];
    const ultimo = enfocables[enfocables.length - 1];
    if (e.shiftKey && (document.activeElement === primero || document.activeElement === this.panel)) {
      e.preventDefault();
      ultimo.focus();
    } else if (!e.shiftKey && document.activeElement === ultimo) {
      e.preventDefault();
      primero.focus();
    }
  }

  /* En celular, deslizar el panel a la derecha lo cierra. No aplica sobre la
     fila de sugerencias, que se desliza en horizontal por si misma. */
  deslizarParaCerrar() {
    if (!this.panel) return;
    let x0 = null;
    let y0 = null;
    let dx = 0;
    this.panel.addEventListener(
      'touchstart',
      (e) => {
        if (e.target.closest('[data-cd-recs-pista]')) {
          x0 = null;
          return;
        }
        x0 = e.touches[0].clientX;
        y0 = e.touches[0].clientY;
        dx = 0;
      },
      { passive: true }
    );
    this.panel.addEventListener(
      'touchmove',
      (e) => {
        if (x0 === null) return;
        dx = e.touches[0].clientX - x0;
        if (Math.abs(e.touches[0].clientY - y0) > Math.abs(dx)) {
          x0 = null;
          this.panel.style.translate = '';
          return;
        }
        if (dx > 0) {
          this.panel.style.transition = 'none';
          this.panel.style.translate = `${dx}px 0`;
        }
      },
      { passive: true }
    );
    this.panel.addEventListener('touchend', () => {
      if (x0 === null) return;
      this.panel.style.transition = '';
      this.panel.style.translate = '';
      if (dx > 90) this.cerrar();
      x0 = null;
    });
  }

  /* ── Pintar lo que devuelve Shopify ── */

  cargando(si) {
    this.classList.toggle('esta-cargando', si);
  }

  refrescar() {
    if (this.refrescando) return this.refrescando;
    const raiz = this.cfg.raiz || '/';
    const url = `${raiz}${raiz.endsWith('/') ? '' : '/'}?sections=${encodeURIComponent(this.sid)}`;
    this.refrescando = fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then((r) => r.json())
      .then((j) => this.aplicar(j[this.sid]))
      .catch(() => {})
      .finally(() => {
        this.refrescando = null;
      });
    return this.refrescando;
  }

  aplicar(html) {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    this.querySelectorAll('[data-cd-live]').forEach((zona) => {
      const nueva = doc.querySelector(`[data-cd-live="${zona.dataset.cdLive}"]`);
      if (!nueva) return;
      zona.hidden = nueva.hidden;
      if (zona.dataset.cdLive === 'envio') {
        // La barra se conserva para que se deslice hasta el valor nuevo en vez
        // de saltar: solo cambian el texto y el ancho.
        const texto = zona.querySelector('[data-cd-envio-texto]');
        const textoNuevo = nueva.querySelector('[data-cd-envio-texto]');
        if (texto && textoNuevo) texto.innerHTML = textoNuevo.innerHTML;
        const relleno = zona.querySelector('[data-cd-relleno]');
        const rellenoNuevo = nueva.querySelector('[data-cd-relleno]');
        if (relleno && rellenoNuevo) relleno.style.width = rellenoNuevo.style.width;
        zona.dataset.cdFalta = nueva.dataset.cdFalta;
        return;
      }
      zona.innerHTML = nueva.innerHTML;
    });

    // Si el repintado se llevo el elemento enfocado, el foco vuelve al panel en
    // vez de perderse en el body, detras del velo.
    if (this.abierto && !this.panel.contains(document.activeElement)) this.panel.focus({ preventScroll: true });

    const cuenta = Number(this.querySelector('[data-cd-cuenta]')?.dataset.cdCuenta || 0);
    this.classList.toggle('is-empty', cuenta === 0);

    // El contador del header va aqui, que es el unico sitio por el que pasan
    // todos los caminos: agregar, cambiar, quitar, deshacer y subir.
    ritActualizarBurbuja(cuenta);
    if (this.abierto) this.cargarRecs();
    return cuenta;
  }

  async post(url, cuerpo) {
    cuerpo.sections = this.sid;
    cuerpo.sections_url = window.location.pathname;
    const r = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(cuerpo),
    });
    const j = await r.json();
    if (!r.ok || j.status) throw j;
    return j;
  }

  textoError(err) {
    return (err && (err.description || err.message)) || this.t.error;
  }

  errorEnLinea(clave, mensaje) {
    const el = this.querySelector(`[data-cd-line][data-key="${CSS.escape(clave)}"] [data-cd-err]`);
    if (el) el.textContent = mensaje;
  }

  anunciar(mensaje) {
    if (!this.estado) return;
    this.estado.textContent = '';
    setTimeout(() => {
      this.estado.textContent = mensaje;
    }, 30);
  }

  lineaDe(vid) {
    return this.querySelector(`[data-cd-line][data-vid="${vid}"]`);
  }

  /* ── Agregar desde la ficha ── */

  /**
   * Se abre de inmediato para que el clic tenga respuesta, pero en estado de
   * carga: sin eso el cliente ve un carrito vacio durante el segundo y medio
   * que tarda el servidor, que es justo lo que rompia la confianza.
   *
   * La promesa del evento sirve para el tiempo, no para los datos: el carrito
   * que trae viene con la forma de la API de Storefront. Se espera y despues se
   * pide la seccion ya pintada por Shopify.
   */
  async alAgregar(evento) {
    const lineas = evento.lines || evento.detail?.lines || [];
    const vids = lineas.map((l) => String(l.merchandiseId || '').split('/').pop()).filter(Boolean);
    this.cargando(true);
    this.abrir();
    let salioBien = true;
    try {
      await evento.promise;
    } catch {
      salioBien = false;
    }
    await this.refrescar();
    this.cargando(false);
    if (salioBien && vids[0]) {
      this.ancla = vids[0];
      this.confirmarAgregado(vids[0]);
    }
    // La clave de las sugerencias incluye el producto ancla: si cambio, se
    // piden de nuevo; si no, no se repite la peticion.
    this.cargarRecs();
  }

  confirmarAgregado(vid) {
    const linea = this.lineaDe(vid);
    if (!linea || !this.agregado) return;
    const nombre = linea.dataset.name + (linea.dataset.vt ? ` · ${linea.dataset.vt}` : '');
    const texto = (this.t.agregaste || '[[p]]').replace('[[p]]', nombre);
    this.agregado.querySelector('[data-cd-agregado-texto]').textContent = texto;
    this.agregado.hidden = false;
    this.agregado.classList.remove('is-on');
    void this.agregado.offsetWidth;
    this.agregado.classList.add('is-on');
    this.anunciar(texto);

    linea.classList.remove('is-new');
    void linea.offsetWidth;
    linea.classList.add('is-new');
    this.scroll?.scrollTo({ top: 0, behavior: this.reduce ? 'auto' : 'smooth' });

    clearTimeout(this.relojAgregado);
    this.relojAgregado = setTimeout(() => {
      this.agregado.classList.remove('is-on');
      setTimeout(() => {
        if (!this.agregado.classList.contains('is-on')) this.agregado.hidden = true;
      }, 350);
    }, 5000);
  }

  /* ── Cantidad, quitar, deshacer y subir de presentacion ── */

  cambiarLinea(linea, cantidad) {
    const clave = linea.dataset.key;
    linea.classList.add('esta-ocupada');
    this.post(this.cfg.rutas.cambiar, { id: clave, quantity: cantidad })
      .then((carrito) => {
        this.aplicar(carrito.sections?.[this.sid]);
        const item = (carrito.items || []).find((i) => i.key === clave);
        if (cantidad > 0 && item && item.quantity < cantidad) this.errorEnLinea(clave, this.t.sinStock);
        this.anunciar(this.t.actualizado);
      })
      .catch((err) => {
        linea.classList.remove('esta-ocupada');
        this.errorEnLinea(clave, this.textoError(err));
      });
  }

  quitarLinea(linea) {
    let propiedades = null;
    try {
      propiedades = JSON.parse(linea.dataset.props || 'null');
    } catch {}
    const info = { id: Number(linea.dataset.vid), quantity: Number(linea.dataset.qty) || 1, nombre: linea.dataset.name };
    if (propiedades && !Array.isArray(propiedades) && Object.keys(propiedades).length) info.properties = propiedades;

    linea.classList.add('esta-ocupada');
    this.post(this.cfg.rutas.cambiar, { id: linea.dataset.key, quantity: 0 })
      .then((carrito) => {
        this.aplicar(carrito.sections?.[this.sid]);
        this.infoDeshacer = info;
        const texto = (this.t.quitaste || '[[p]]').replace('[[p]]', info.nombre);
        this.barraDeshacer.querySelector('[data-cd-deshacer-texto]').textContent = texto;
        this.barraDeshacer.querySelector('[data-cd-deshacer]').hidden = false;
        this.barraDeshacer.hidden = false;
        this.anunciar(texto);
        clearTimeout(this.relojDeshacer);
        this.relojDeshacer = setTimeout(() => {
          this.barraDeshacer.hidden = true;
          this.infoDeshacer = null;
        }, 7000);
      })
      .catch((err) => {
        linea.classList.remove('esta-ocupada');
        this.errorEnLinea(linea.dataset.key, this.textoError(err));
      });
  }

  deshacer() {
    const info = this.infoDeshacer;
    if (!info) return;
    this.infoDeshacer = null;
    this.barraDeshacer.hidden = true;
    const item = { id: info.id, quantity: info.quantity };
    if (info.properties) item.properties = info.properties;
    this.post(this.cfg.rutas.agregar, { items: [item] })
      .then((res) => {
        this.aplicar(res.sections?.[this.sid]);
        this.anunciar((this.t.volvio || '[[p]]').replace('[[p]]', info.nombre));
      })
      .catch((err) => {
        this.barraDeshacer.querySelector('[data-cd-deshacer-texto]').textContent = this.textoError(err);
        this.barraDeshacer.querySelector('[data-cd-deshacer]').hidden = true;
        this.barraDeshacer.hidden = false;
        clearTimeout(this.relojDeshacer);
        this.relojDeshacer = setTimeout(() => {
          this.barraDeshacer.hidden = true;
        }, 5000);
      });
  }

  /**
   * Subir de 300 a 500 mL sin borrar la linea. Si la de 500 ya estaba en el
   * carrito, las unidades se suman a esa en vez de dejar dos lineas del mismo
   * producto. Es la misma operacion que "cambiar talla" en VOLTAJE.
   */
  subir(linea, boton) {
    const destino = boton.dataset.upVid;
    const cantidad = Number(linea.dataset.qty) || 1;
    const otra = this.lineaDe(destino);
    const updates = { [linea.dataset.key]: 0, [destino]: cantidad + (otra ? Number(otra.dataset.qty) || 0 : 0) };
    linea.classList.add('esta-ocupada');
    this.post(this.cfg.rutas.actualizar, { updates })
      .then((carrito) => {
        this.aplicar(carrito.sections?.[this.sid]);
        const nueva = this.lineaDe(destino);
        if (nueva) {
          nueva.classList.add('is-new');
          this.anunciar((this.t.subiste || '[[t]]').replace('[[t]]', boton.dataset.upTalla));
        }
      })
      .catch((err) => {
        linea.classList.remove('esta-ocupada');
        this.errorEnLinea(linea.dataset.key, this.textoError(err));
      });
  }

  /* ── Sugerencias ── */

  normalizar(texto) {
    return String(texto || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase();
  }

  /** Reglas del editor, una por linea: "palabras del carrito > palabras a sugerir". */
  objetivos(lineas) {
    const titulos = lineas.map((l) => this.normalizar(l.dataset.name));
    const objetivos = new Set();
    String(this.cfg.reglas || '')
      .split('\n')
      .forEach((regla) => {
        const [izq, der] = regla.split('>');
        if (!izq || !der) return;
        const si = izq.split(',').map((x) => this.normalizar(x).trim()).filter(Boolean);
        const sugerir = der.split(',').map((x) => this.normalizar(x).trim()).filter(Boolean);
        if (titulos.some((t) => si.some((k) => t.includes(k)))) sugerir.forEach((k) => objetivos.add(k));
      });
    return [...objetivos];
  }

  tarjetasDe(html) {
    const doc = new DOMParser().parseFromString(html || '', 'text/html');
    return [...doc.querySelectorAll('[data-mc]')];
  }

  pedirTarjetas(url) {
    return fetch(url)
      .then((r) => (r.ok ? r.text() : ''))
      .then((h) => this.tarjetasDe(h))
      .catch(() => []);
  }

  /** El grupo de la coleccion se guarda diez minutos: no cambia entre un clic y otro. */
  grupo(url) {
    this.grupos = this.grupos || {};
    if (this.grupos[url]) return this.grupos[url];
    let guardado = null;
    try {
      guardado = JSON.parse(sessionStorage.getItem('rit_cd_grupo') || 'null');
    } catch {}
    if (guardado && guardado.u === url && Date.now() - guardado.t < 600000) {
      this.grupos[url] = Promise.resolve(this.tarjetasDe(guardado.h));
    } else {
      this.grupos[url] = fetch(url)
        .then((r) => (r.ok ? r.text() : ''))
        .then((h) => {
          try {
            sessionStorage.setItem('rit_cd_grupo', JSON.stringify({ t: Date.now(), u: url, h }));
          } catch {}
          return this.tarjetasDe(h);
        })
        .catch(() => []);
    }
    return this.grupos[url];
  }

  urlRecomendacion(pid, intencion) {
    const base = this.cfg.rutas.recs || '/recommendations/products';
    return `${base}?section_id=rit-cart-recs&limit=10&intent=${intencion}&product_id=${pid}`;
  }

  /**
   * Cascada de Ritualic. Nunca ofrece lo que ya esta en el carrito.
   *  1. Complementarios de Search & Discovery para el ultimo producto agregado.
   *  2. Las reglas del editor (champu con acondicionador, ritual con tonicos...).
   *  3. Relacionados de Shopify, si con lo anterior no se llena la fila.
   * Y si al pedido le falta para el envio gratis, primero va lo que lo alcanza
   * con el menor sobrecosto, marcado "Con este, envio gratis".
   * Carrito vacio: los mas vendidos de la coleccion.
   */
  async cargarRecs() {
    if (!this.cfg.recs || !this.recs) return;
    const lineas = [...this.querySelectorAll('[data-cd-line]')];
    const enCarrito = new Set(lineas.map((l) => l.dataset.pid));
    const ancla = (this.ancla && this.lineaDe(this.ancla)) || lineas[0];
    const falta = Math.max(0, Number(this.querySelector('[data-cd-live="envio"]')?.dataset.cdFalta || 0));
    const clave = `${[...enCarrito].join(',')}|${ancla?.dataset.pid || ''}|${falta}`;
    if (clave === this.recsClave) return;
    this.recsClave = clave;

    if (!ancla) {
      const masVendidos = await this.grupo(this.cfg.vacioUrl);
      return this.mostrar(masVendidos.slice(0, 4), this.cfg.tituloVacio, clave, 0);
    }

    const elegidas = new Map();
    const sumar = (lista) =>
      lista.forEach((c) => {
        if (!enCarrito.has(c.dataset.pid) && !elegidas.has(c.dataset.pid)) elegidas.set(c.dataset.pid, c);
      });

    sumar(await this.pedirTarjetas(this.urlRecomendacion(ancla.dataset.pid, 'complementary')));

    const objetivos = this.objetivos(lineas);
    if (objetivos.length) {
      const grupo = await this.grupo(this.cfg.grupoUrl);
      sumar(grupo.filter((c) => objetivos.some((k) => this.normalizar(c.dataset.titulo).includes(k))));
    }

    let titulo = this.cfg.titulo;
    const limite = Number(this.cfg.limite) || 6;
    if (elegidas.size < limite) {
      const antes = elegidas.size;
      sumar(await this.pedirTarjetas(this.urlRecomendacion(ancla.dataset.pid, 'related')));
      if (antes === 0 && elegidas.size) titulo = this.cfg.tituloRelacionados;
    }

    // Copias: las tarjetas del grupo viven en cache y marcarlas ahi dejaria la
    // insignia de envio gratis pegada en la siguiente vuelta.
    let lista = [...elegidas.values()].map((c) => c.cloneNode(true));
    if (falta > 0) {
      // Solo las dos que cierran el envio con menor sobrecosto van al frente. Si
      // se les dejara toda la fila, un champu de $50.000 se quedaba sin su
      // acondicionador, que es su pareja natural pero no alcanza el umbral solo.
      const cierran = lista
        .filter((c) => Number(c.dataset.precio) >= falta)
        .sort((a, b) => Number(a.dataset.precio) - Number(b.dataset.precio))
        .slice(0, 2);
      cierran.forEach((c) => c.querySelector('[data-mc-cierra]')?.removeAttribute('hidden'));
      lista = cierran.concat(lista.filter((c) => !cierran.includes(c)));
    }
    const fijo = lista.find((c) => c.dataset.pid === '15428837835006');
    if (fijo) lista = [fijo].concat(lista.filter((c) => c !== fijo));
    this.mostrar(lista.slice(0, limite), titulo, clave);
  }

  mostrar(tarjetas, titulo, clave) {
    if (!this.recs || clave !== this.recsClave) return;
    this.pista.replaceChildren(...tarjetas.map((c) => document.importNode(c, true)));
    this.recsTitulo.textContent = titulo || '';
    this.recs.hidden = !tarjetas.length;
    this.pista.scrollLeft = 0;
    this.flechas();
  }

  flechas() {
    if (!this.pista) return;
    const max = this.pista.scrollWidth - this.pista.clientWidth;
    const caja = this.recs.querySelector('.rit-cd__recs-flechas');
    if (caja) caja.hidden = max < 4;
    this.recs.querySelectorAll('[data-cd-recs-nav]').forEach((b) => {
      b.disabled = b.dataset.cdRecsNav === '-1' ? this.pista.scrollLeft < 4 : this.pista.scrollLeft > max - 4;
    });
  }

  variantesDe(tarjeta) {
    if (!tarjeta.variantes) {
      try {
        tarjeta.variantes = JSON.parse(tarjeta.querySelector('[data-mc-json]').textContent);
      } catch {
        tarjeta.variantes = [];
      }
    }
    return tarjeta.variantes;
  }

  /**
   * El precio de la tarjeta se reescribe con `shop.money_format`, el mismo
   * ajuste que usa el filtro `money` de Liquid. Intl no sirve: el documento
   * declara `lang="es"` a secas y un peso sale como "195.000 COP" mientras la
   * ficha muestra "$195.000".
   */
  dinero(centavos) {
    const formato = this.cfg.formato || '${{amount_no_decimals_with_comma_separator}}';
    const valor = centavos / 100;
    const conSeparadores = (n, decimales, miles, decimal) => {
      const [entero, resto] = n.toFixed(decimales).split('.');
      const agrupado = entero.replace(/\B(?=(\d{3})+(?!\d))/g, miles);
      return resto ? agrupado + decimal + resto : agrupado;
    };
    const piezas = {
      amount: () => conSeparadores(valor, 2, ',', '.'),
      amount_no_decimals: () => conSeparadores(valor, 0, ',', '.'),
      amount_with_comma_separator: () => conSeparadores(valor, 2, '.', ','),
      amount_no_decimals_with_comma_separator: () => conSeparadores(valor, 0, '.', ','),
      amount_with_space_separator: () => conSeparadores(valor, 2, ' ', ','),
      amount_no_decimals_with_space_separator: () => conSeparadores(valor, 0, ' ', ','),
      amount_with_apostrophe_separator: () => conSeparadores(valor, 2, "'", '.'),
    };
    return formato.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, clave) =>
      piezas[clave] ? piezas[clave]() : conSeparadores(valor, 0, '.', ',')
    );
  }

  elegirPresentacion(tarjeta, boton) {
    const variante = this.variantesDe(tarjeta).find((v) => String(v.id) === boton.dataset.mcPresentacion);
    if (!variante) return;
    tarjeta.querySelectorAll('[data-mc-presentacion]').forEach((b) => b.setAttribute('aria-pressed', String(b === boton)));
    tarjeta.dataset.vid = String(variante.id);
    tarjeta.dataset.precio = String(variante.p);
    const precio = tarjeta.querySelector('[data-mc-precio]');
    if (precio) {
      precio.textContent = this.dinero(variante.p);
      if (variante.c > variante.p) {
        const tachado = document.createElement('s');
        tachado.textContent = this.dinero(variante.c);
        precio.append(' ', tachado);
      }
    }
  }

  agregarSugerencia(tarjeta, boton) {
    const vid = tarjeta.dataset.vid;
    const mensaje = tarjeta.querySelector('[data-mc-msg]');
    boton.classList.add('esta-ocupado');
    this.post(this.cfg.rutas.agregar, { items: [{ id: Number(vid), quantity: 1 }] })
      .then((res) => {
        tarjeta.classList.add('esta-agregada');
        boton.classList.remove('esta-ocupado');
        if (mensaje) mensaje.textContent = this.t.agregado;
        this.ancla = vid;
        this.aplicar(res.sections?.[this.sid]);
        this.confirmarAgregado(vid);
      })
      .catch((err) => {
        boton.classList.remove('esta-ocupado');
        if (mensaje) mensaje.textContent = this.textoError(err);
      });
  }

  /* ── Clics y envio ── */

  alClic(e) {
    const t = e.target;
    let el;
    if (t.closest('[data-rit-cd="cerrar"]')) return this.cerrar();
    if (t.closest('[data-cd-deshacer]')) return this.deshacer();
    if ((el = t.closest('[data-cd-recs-nav]'))) {
      this.pista.scrollBy({
        left: Number(el.dataset.cdRecsNav) * this.pista.clientWidth * 0.8,
        behavior: this.reduce ? 'auto' : 'smooth',
      });
      return;
    }

    const tarjeta = t.closest('[data-mc]');
    if (tarjeta) {
      if ((el = t.closest('[data-mc-presentacion]'))) return this.elegirPresentacion(tarjeta, el);
      if ((el = t.closest('button[data-mc-agregar]'))) return this.agregarSugerencia(tarjeta, el);
      return;
    }

    const linea = t.closest('[data-cd-line]');
    if (!linea) return;
    if ((el = t.closest('[data-cd-qty]'))) {
      const nueva = (Number(linea.dataset.qty) || 0) + Number(el.dataset.cdQty);
      if (nueva <= 0) this.quitarLinea(linea);
      else this.cambiarLinea(linea, nueva);
      return;
    }
    if (t.closest('[data-cd-remove]')) return this.quitarLinea(linea);
    if ((el = t.closest('[data-cd-subir]'))) return this.subir(linea, el);
  }

  alEnviar(e) {
    const boton = e.target.querySelector('[data-cd-pagar]');
    if (!boton) return;
    // No se desactiva: un boton desactivado no viaja en el formulario y Shopify
    // no iria al pago. Solo se evita el doble envio.
    if (boton.classList.contains('esta-ocupado')) {
      e.preventDefault();
      return;
    }
    boton.classList.add('esta-ocupado');
    boton.setAttribute('aria-busy', 'true');
    setTimeout(() => {
      boton.classList.remove('esta-ocupado');
      boton.removeAttribute('aria-busy');
    }, 6000);
  }
}

if (!customElements.get('rit-cart-drawer')) customElements.define('rit-cart-drawer', RitCartDrawer);
